import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, IconButton } from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { getAllGroups, getAllGroupsForUser } from '../graphql/queries';
import { API } from '../services/amplifyCompat';
import GroupCard from '../components/GroupCard';
import { useUserInfoContext } from '../hooks/use-user-info-context';
import useTranslation from '../components/customHooks/translations';

const FindGroup = () => {
  const { user } = useUserInfoContext();
  const [groups, setGroups] = useState();
  const [usersGroups, setUsersGroups] = useState();
  const [input, setInput] = useState('');
  const [filteredGroups, setFilteredGroups] = useState();
  const [error, setError] = useState(false);

  const translation = useTranslation();

  useEffect(() => {
    const getGroups = async () => {
      const [allGroupsRes, usersGroupsRes] = await Promise.all([
        API.graphql({
          query: getAllGroups,
        }),
        API.graphql({
          query: getAllGroupsForUser,
          variables: { user_id: user.user_id },
        }),
      ]);

      setGroups(allGroupsRes.data.getAllGroups);
      setUsersGroups(usersGroupsRes.data.getAllGroupsForUser);

      const filtered = allGroupsRes.data.getAllGroups.filter((group) => {
        return group.is_public;
      });

      const sorted = filtered.sort((a, b) => {
        return b.monthly_points - a.monthly_points;
      });

      sorted.length > 5
        ? setFilteredGroups(sorted.slice(0, 5))
        : setFilteredGroups(sorted);
    };
    user && getGroups();
  }, [user]);

  useEffect(() => {
    renderFilteredGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, groups]);

  const renderFilteredGroups = () => {
    if (!groups) {
      return;
    }

    if (input === '') {
      setError(false);
      const filtered = groups.filter((group) => {
        return group.is_public;
      });

      const sorted = filtered.sort((a, b) => {
        return b.monthly_points - a.monthly_points;
      });
      // Top groups preview when search is empty
      sorted.length > 5
        ? setFilteredGroups(sorted.slice(0, 5))
        : setFilteredGroups(sorted);
      return;
    }

    const filtered = groups.filter((group) => {
      return (
        group.is_public &&
        group.group_name.toLowerCase().includes(input.toLowerCase())
      );
    });
    setFilteredGroups(filtered);
    // Clear error when results appear; show no-results when empty
    setError(filtered.length === 0);
  };

  return (
    <>
      <Box sx={{ textAlign: { xs: 'center' } }}>
        <Typography variant="h1" sx={{ mt: { xs: '1.5em', md: '0' } }}>
          {translation.findGroupTitle}
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: { xs: '3em' } }}>
          {translation.findGroupDescription}
        </Typography>
        <TextField
          id="outlined-basic"
          variant="outlined"
          fullWidth
          label={translation.search}
          value={input}
          sx={{ my: '2em' }}
          onChange={(e) => setInput(e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: <Search sx={{ mr: '1em' }} />,
            endAdornment: input && (
              <IconButton
                aria-label={translation.clearSearch}
                onClick={() => setInput('')}
              >
                <Clear />
              </IconButton>
            ),
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
        {error && (
          <Typography variant="subtitle2">
            {translation.formatString(translation.findGroupErrorSearch, input)}
          </Typography>
        )}
      </Box>

      {(() => {
        if (input === '') {
          return (
            <Typography variant="subtitle2" my={'1rem'}>
              {translation.findGroupTopGroups}
            </Typography>
          );
        } else {
          return (
            <Typography variant="subtitle2" my={'1rem'}>
              {translation.searchResults}
            </Typography>
          );
        }
      })()}

      {filteredGroups &&
        usersGroups &&
        !error &&
        filteredGroups.map((group) => (
          <GroupCard
            key={group.group_id}
            group={group}
            joinGroupOption={
              !usersGroups.some(
                (userGroup) => userGroup.group_id === group.group_id
              )
            }
            user={user}
          />
        ))}
    </>
  );
};

export default FindGroup;
